// libs
import * as React from "react";
import * as showdown from "showdown";

// interfaces / constants
import {
  IBBCode,
  IBBCodes,
  IBodyMarkdown,
  IIngredient,
} from "src/interfaces/post_ingredients";
import {
  mapDisplayModeToConst,
  POST_DISPLAY_MODE_DETAIL,
} from "src/interfaces/posts";

// constants
import { CLASS_PREFIX } from "src/constants/";

// classes
import BlurredContent from "src/components/blurred_content/blurred_content";
import NavigationItem from "src/components/navigation_item/navigation_item";

// styles
import "./body_markdown.scss";

export type IProps = IIngredient<IBodyMarkdown>;

export const cls = `${CLASS_PREFIX}body-markdown__`;

const escapeReplaceMap: { [key: string]: string } = {
  "\n": "\n",
  ">": ">",
};
const escapeFilterEx = new RegExp(Object.keys(escapeReplaceMap).join("|"), "g");
const showdownOptions: showdown.ConverterOptions = {
  noHeaderId: true,
  openLinksInNewWindow: true,
  simplifiedAutoLink: true,
  tables: true,
};

const showdownConverter = new showdown.Converter(showdownOptions);

// Function to extract YouTube video ID from URL
function extractYouTubeVideoId(url: string): string | null {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

export default class BodyMarkdown extends React.PureComponent<IProps, {}> {
  private bodyMarkdown: React.RefObject<HTMLDivElement> = React.createRef();
  private setIframeHeightFromItsContent: (event: MessageEvent) => void;
  private cls = "";
  private clsIframe = "";

  constructor(props: IProps) {
    super(props);
    this.cls = `${cls}${mapDisplayModeToConst[props.displayMode]}`;
    this.clsIframe = `${this.cls}__iframe`;
  }

  public render() {
    const {
      blurred,
      data: { bbcodes, fullContent, teaserContent },
      displayMode,
      postDetailsURL,
    } = this.props;

    const htmlContent: string =
      displayMode === POST_DISPLAY_MODE_DETAIL
        ? this.parseContent(fullContent, bbcodes)
        : teaserContent;
    const bodyElement = (
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    );

    return (
      <NavigationItem target="_self" url={postDetailsURL}>
        <div className={this.cls} ref={this.bodyMarkdown}>
          {blurred ? (
            <BlurredContent blurred={blurred}>{bodyElement}</BlurredContent>
          ) : (
            bodyElement
          )}
        </div>
      </NavigationItem>
    );
  }

  public componentDidMount() {
    window.addEventListener("message", this.handleIframeMessage, false);
  }

  private handleIframeMessage = (event: MessageEvent) => {
    const { id, height } = event.data || {};
    if (typeof height !== "number" || !id) return;
    const iframe = this.bodyMarkdown.current?.querySelector(
      `iframe#${id}`
    ) as HTMLIFrameElement | null;
    if (iframe) {
      console.log("height::", height, "id::", id);
      iframe.style.height = `${height}px`;
    }
  };

  private parseContent(content: string, bbcodes: IBBCodes): string {
    const afterEscapeParse: string = this.parseEscapes(content);
    return this.parseMarkdownWithBBCodes(afterEscapeParse, bbcodes);
  }

  private parseEscapes(content: string): string {
    return content.replace(
      escapeFilterEx,
      (match: string): string => escapeReplaceMap[match]
    );
  }

  private parseMarkdownWithBBCodes(content: string, bbcodes: IBBCodes): string {
    const converted: string = showdownConverter.makeHtml(content);
    if (!bbcodes) {
      return converted;
    }

    return converted.replace(/{{[\w\d_-]+}}/g, (match: string) =>
      this.replaceWithBBCodes(match, bbcodes)
    );
  }

  private replaceWithBBCodes(match: string, bbcodes: IBBCodes): string {
    if (!bbcodes.hasOwnProperty(match)) {
      return match;
    }

    const bbCode: IBBCode = bbcodes[match];

    if (bbCode.errors) {
      return bbCode.errors.join(", ");
    }
    if (!bbCode.data || !bbCode.id) {
      return "no data";
    }

    const sandboxUrl: string = this.getSandboxUrl(bbCode);
    const sandBox = `<iframe id='${bbCode.id}'
                             class='${this.clsIframe}'
                             scrolling='no'
                             src='${sandboxUrl}'>
                      </iframe>`;
    return sandBox;
  }

  private getSandboxUrl(bbObject: IBBCode): string {
    if (!bbObject.data) {
      return "";
    }

    switch (bbObject.type) {
      case "html":
        return bbObject.data.urls.resource;
      case "facebook":
        return `/static/facebook-embed.html#${bbObject.data.url}`;
      case "instagram":
        return `/static/instagram-embed.html#${bbObject.data.url}`;

      case "image": {
        const html = `
          <!doctype html>
          <html>
            <head>
              <meta name="viewport" content="width=device-width,initial-scale=1">
              <style>
                body {
                  margin: 0;
                  padding: 0px;
                  box-sizing: border-box;
                  font-family:sans-serif
                }
                img {
                  max-width: 100%;
                  height: auto;
                  display: block;
                  margin: 0 auto;
                }
              </style>
            </head>
            <body>
              <img
                src="${bbObject.data.url}"
                alt="${bbObject.data.alt || ""}"
              />
              ${
                bbObject.data.caption
                  ? `<p style="text-align:center;color:rgb(5, 5, 5);margin-top:12px;margin-bottom:2px;font-size:12px">
                    ${bbObject.data.caption}
                  </p>`
                  : ""
              }
              ${
                bbObject.data.source
                  ? `<p style="text-align:center;color:rgb(20, 20, 20);margin-top:0px;font-size:10px">
                    ${bbObject.data.source}
                  </p>`
                  : ""
              }
              <script>
                function sendHeight() {
                  parent.postMessage(
                    { id: "${bbObject.id}", height: document.body.scrollHeight },
                    "*"
                  );
                }
                window.addEventListener("load", sendHeight);
                new ResizeObserver(sendHeight).observe(document.body);
              </script>
            </body>
          </html>
        `;
        return `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
      }
      case "youtube": {
        let videoId = bbObject.data.video_id;
        if (!videoId && bbObject.data.url) {
          videoId = extractYouTubeVideoId(bbObject.data.url);
        }
        if (!videoId) {
          return "Invalid YouTube URL";
        }

        const html = `
          <!doctype html>
          <html>
            <head>
              <meta name="viewport" content="width=device-width,initial-scale=1">
              <style>
                body {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                  font-family: sans-serif;
                  background: #000;
                }
                .video-container {
                  position: relative;
                  padding-bottom: 56.25%;
                  height: 0;
                  overflow: hidden;
                }
                iframe {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  border: none;
                }
              </style>
            </head>
            <body>
              <div class="video-container">
                <iframe
                  src="https://www.youtube.com/embed/${videoId}?rel=0"
                  allowfullscreen
                ></iframe>
              </div>
              <script>
                function sendHeight() {
                  parent.postMessage(
                    { id: "${bbObject.id}", height: document.body.scrollHeight },
                    "*"
                  );
                }
                window.addEventListener("load", sendHeight);
                new ResizeObserver(sendHeight).observe(document.body);
              </script>
            </body>
          </html>
        `;

        return `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
      }
      default:
        return "not a recognized type";
    }
  }
}
